import type { CenterPatient } from "../../center";

/**
 * One person in the centre's directory, and every record it holds for them.
 *
 * A centre keeps one `Patient` row per therapeutic relationship, so somebody
 * seen by two of its professionals is two rows. Listing them as two entries
 * presents one person as two strangers who happen to share a name; this groups
 * them back together for the directory, and nothing more.
 *
 * It is a directory grouping, never a merged record. The relationships stay
 * separate and are never summed: one "12 sessions" across two professionals
 * would read as a single therapy, which is exactly what it is not.
 */
/**
 * How many records to ask for when a screen needs the whole caseload at once —
 * to group it by person, or to offer everyone in a filter. The directory is
 * read a page at a time elsewhere; these screens page what they are given.
 */
export const WHOLE_CASELOAD = 1000;

export interface CaseloadPerson {
    /** The grouping key, or the record id when there was nothing to group by. */
    key: string;
    firstName: string;
    lastName: string;
    email: string | null;
    /** Whether this row really is one person, or one record standing alone. */
    grouped: boolean;
    records: CenterPatient[];
}

/**
 * The address, comparable.
 *
 * Returns null for anything that cannot identify somebody: a centre may hold
 * records with no address at all, and grouping those together would merge
 * strangers.
 */
export function personKey(patient: CenterPatient): string | null {
    const email = patient.contact_email?.trim().toLowerCase();
    return email ? email : null;
}

/**
 * Group the centre's records by the person they belong to.
 *
 * Only an address groups. Names do not: two Ana Garcías are not one person,
 * and guessing wrong would show one patient's professional beside another
 * patient's name. A record with no address stays on its own — the directory
 * would rather show somebody twice than show two people as one.
 */
export function groupByPerson(patients: CenterPatient[]): CaseloadPerson[] {
    const byKey = new Map<string, CaseloadPerson>();

    for (const patient of patients) {
        const key = personKey(patient);
        const entry = key === null ? `record:${patient.patient_id}` : `person:${key}`;

        const existing = byKey.get(entry);
        if (existing) {
            existing.records.push(patient);
            existing.grouped = true;
            continue;
        }

        byKey.set(entry, {
            key: entry,
            firstName: patient.first_name,
            lastName: patient.last_name,
            email: key,
            grouped: false,
            records: [patient],
        });
    }

    return [...byKey.values()];
}
